import type { RenderCommand } from "../workers/dispatch/types";

import { mapRenderer } from "./mapRenderer";
import { sidebarRenderer } from "./sidebarRenderer";

class RenderLoop {
  enqueue(cmd: RenderCommand): void {
    this.queue.push(cmd);
    if (!this.running) this.start();
  }

  private queue: RenderCommand[] = [];
  private rafId: number | null = null;
  private running = false;

  start(): void {
    if (this.running) return;
    this.running = true;
    this.rafId = requestAnimationFrame(this.tick);
  }

  stop(): void {
    if (this.rafId !== null) {
      cancelAnimationFrame(this.rafId);
      this.rafId = null;
    }
    this.running = false;
    this.queue.length = 0;
  }

  private tick = (): void => {
    // Drain everything queued since last frame
    const batch = this.queue.splice(0);

    for (const cmd of batch) {
      switch (cmd.type) {
        case "INIT_MARKERS":
          mapRenderer.applyInit(cmd.markers);
          break;

        case "UPDATE_MARKERS":
          mapRenderer.applyUpdates(cmd.updates);
          break;

        case "UPDATE_CLUSTERS":
          mapRenderer.applyClusters(cmd.clusters, cmd.individualOrderIds);
          break;

        case "UPDATE_SIDEBAR":
          sidebarRenderer.applySidebarResult(cmd.result);
          break;

        case "UPDATE_VIEWPORT":
          // Viewport visibility is tracked inside mapRenderer via applyClusters.
          // A dedicated UPDATE_VIEWPORT gives us the raw visible set if needed.
          break;
      }
    }

    // Imperative per-frame work
    sidebarRenderer.tick();

    if (this.queue.length > 0) {
      // More commands arrived while we were processing — schedule next frame
      this.rafId = requestAnimationFrame(this.tick);
    } else {
      this.running = false;
      this.rafId = null;
    }
  };
}

export const renderLoop = new RenderLoop();
